import { useCallback, useEffect, useState } from 'react';
import { onBalanceUpdate, onTokenCreated, BalanceUpdateDetail } from '@/utils/lightEventEmitter';

/**
 * Client-only novice guidance for the mint → share → redeem model.
 *
 * Storage (v2):
 * - dismissed: user tapped "Don't show again"
 * - milestoneCompleted: first token mint (token:created) or redeem (balance reason token_redemption)
 *
 * Do not use profile recentTransactions / counterparties (platform).
 */
const STORAGE_DISMISSED = 'vaiu.paymentsGuide.v2.dismissed';
const STORAGE_MILESTONE = 'vaiu.paymentsGuide.v2.milestoneCompleted';

function readFlag(key: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function writeFlag(key: string, value: boolean): void {
  try {
    if (value) window.localStorage.setItem(key, '1');
    else window.localStorage.removeItem(key);
  } catch {
    /* ignore quota / private mode */
  }
}

export function markPaymentsParadigmGuideMilestoneCompleted(): void {
  writeFlag(STORAGE_MILESTONE, true);
}

export function usePaymentsParadigmGuideVisibility(): {
  visible: boolean;
  dismiss: () => void;
} {
  const [suppressed, setSuppressed] = useState(() =>
    readFlag(STORAGE_DISMISSED) || readFlag(STORAGE_MILESTONE)
  );

  useEffect(() => {
    const offToken = onTokenCreated(() => {
      markPaymentsParadigmGuideMilestoneCompleted();
      setSuppressed(true);
    });
    const offBalance = onBalanceUpdate((detail?: BalanceUpdateDetail) => {
      if (detail?.reason === 'token_redemption') {
        markPaymentsParadigmGuideMilestoneCompleted();
        setSuppressed(true);
      }
    });
    return () => {
      offToken();
      offBalance();
    };
  }, []);

  const dismiss = useCallback(() => {
    writeFlag(STORAGE_DISMISSED, true);
    setSuppressed(true);
  }, []);

  return {
    visible: !suppressed,
    dismiss,
  };
}
